import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@besiktning/ui'
import { apiUrl } from '../lib/apiBase'
import { identifyStaff, groupBusiness, resetAnalytics } from '../lib/analytics'

export interface Membership {
  id: number
  type: 'garage' | 'car_care'
  role: string
  business: {
    id: number
    name: string
    type: string
    kommun?: string
  }
}

export interface CurrentMembership {
  type: 'garage' | 'car_care'
  id: number
}

interface AuthState {
  user: User | null
  token: string | null
  memberships: Membership[]
  currentMembership: CurrentMembership | null
  isAuthenticated: boolean
  isInitializing: boolean
  requiresMembershipSelection: boolean
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  setMemberships: (memberships: Membership[]) => void
  setCurrentMembership: (membership: CurrentMembership | null) => void
  login: (user: User, token: string, memberships: Membership[], currentMembership: CurrentMembership | null, requiresSelection: boolean) => void
  selectMembership: (membershipType: 'garage' | 'car_care', membershipId: number) => Promise<void>
  logout: () => void
  initialize: () => Promise<void>
}

// Get token from localStorage on initialization
const getInitialToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('auth_token')
  }
  return null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: getInitialToken(),
      memberships: [],
      currentMembership: null,
      isAuthenticated: false,
      isInitializing: true,
      requiresMembershipSelection: false,
      setUser: (user) => set({ user }),
      setToken: (token) => {
        // Also sync to localStorage for the API client interceptor
        if (token) {
          localStorage.setItem('auth_token', token)
        } else {
          localStorage.removeItem('auth_token')
        }
        set({ token })
      },
      setMemberships: (memberships) => set({ memberships }),
      setCurrentMembership: (currentMembership) => set({ currentMembership }),
      login: (user, token, memberships, currentMembership, requiresSelection) => {
        localStorage.setItem('auth_token', token)
        identifyStaff(user, currentMembership)
        set({
          user, 
          token, 
          memberships,
          currentMembership,
          isAuthenticated: true, 
          isInitializing: false,
          requiresMembershipSelection: requiresSelection
        })
      },
      selectMembership: async (membershipType, membershipId) => {
        const token = get().token
        if (!token) throw new Error('Not authenticated')
        
        const response = await fetch(apiUrl('/auth/select_membership'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            membership_type: membershipType,
            membership_id: membershipId
          })
        })
        
        if (!response.ok) {
          throw new Error('Failed to select membership')
        }
        
        const data = await response.json()
        
        // Refresh user data after selecting membership
        const meResponse = await fetch(apiUrl('/auth/me'), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (meResponse.ok) {
          const meData = await meResponse.json()
          // Re-group events onto the newly selected garage/car-care center.
          groupBusiness(data.current_membership)
          set({
            user: meData.user,
            currentMembership: data.current_membership,
            requiresMembershipSelection: false
          })
        }
      },
      logout: () => {
        localStorage.removeItem('auth_token')
        resetAnalytics()
        set({
          user: null, 
          token: null, 
          memberships: [],
          currentMembership: null,
          isAuthenticated: false, 
          isInitializing: false,
          requiresMembershipSelection: false
        })
      },
      initialize: async () => {
        const token = localStorage.getItem('auth_token')
        if (token) {
          try {
            // Validate token by fetching user data
            const response = await fetch(apiUrl('/auth/me'), {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
            
            if (response.ok) {
              const data = await response.json()
              // Re-identify a returning staff user on warm start.
              identifyStaff(data.user, data.current_membership)
              set({
                token,
                user: data.user,
                memberships: data.memberships || [],
                currentMembership: data.current_membership,
                isAuthenticated: true, 
                isInitializing: false,
                requiresMembershipSelection: data.requires_membership_selection || false
              })
            } else {
              // Token is invalid
              localStorage.removeItem('auth_token')
              set({ token: null, user: null, memberships: [], currentMembership: null, isAuthenticated: false, isInitializing: false })
            }
          } catch (error) {
            console.error('Initialize error:', error)
            // Network error, keep token for retry but mark as not authenticated
            set({ token, isAuthenticated: false, isInitializing: false })
          }
        } else {
          set({ isInitializing: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      // Only persist user to storage, token is handled separately via localStorage
      partialize: (state) => ({ user: state.user, currentMembership: state.currentMembership }),
    }
  )
)
